import { InputNumber, type InputNumberProps } from 'antd';
import Decimal from 'decimal.js-light';
import { useCallback, useMemo, useRef } from 'react';
import { useInputController } from '../hooks/useInputController.ts';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility.tsx';
import { useFormItemEnabled } from '../item/useFormItemEnabled.ts';

export interface CurrencyInputProps
  extends Omit<InputNumberProps, 'name' | 'type'> {
  name: string;
  currency?: string;
  // Numero di cifre decimali fisse da mostrare
  decimalPlaces?: number;
  hideCurrency?: boolean;
  /** Funzione che trasforma il valore in input */
  transformValue?:
    | ((parsedValue: string | null) => number | string | null)
    | null;
}

/**
 * Campo per input di tipo Valuta.
 */
export function CurrencyInput(props: CurrencyInputProps) {
  const {
    name,
    currency,
    decimalPlaces,
    hideCurrency,
    transformValue,
    onFocus: propsOnFocus,
    ...inputProps
  } = props;

  const hasFixedDecimalPlaces = typeof decimalPlaces === 'number';
  const decimalRegex = useMemo(() => {
    if (!hasFixedDecimalPlaces) return /(?:)/;
    // Regex che viene usato nel `formatted`
    // per rimuovere le cifre decimali dopo la n-esima dove n = decimalPlaces
    // return new RegExp(`\\.(\\d{${decimalPlaces}})(\\d+)`, 'g');
    return new RegExp(`^\\d*\\.?\\d{0,${decimalPlaces}}`);
  }, [decimalPlaces, hasFixedDecimalPlaces]);

  const { enabled, visuallyDisabled } = useFormItemEnabled();

  const {
    field: { value, onChange, onBlur, ref },
  } = useInputController(name, { enabled });

  const accessibility = useFormItemAccessibility();
  const focusedRef = useRef(false);

  // Forzo il salvataggio del dato il dato come numero
  const handleChange = useCallback(
    (value: any) => {
      let parsedValue =
        value === '' || value == null ? null : new Decimal(value);
      if (parsedValue !== null && hasFixedDecimalPlaces) {
        parsedValue = parsedValue.toDecimalPlaces(
          decimalPlaces,
          Decimal.ROUND_DOWN,
        );
      }
      // .toString() per evitare che istanze Decimal finiscano nel form state
      // (structuredClone non supporta classi custom → DataCloneError)
      const serializable = parsedValue?.toString() ?? null;
      onChange(transformValue?.(serializable) ?? serializable);
    },
    [decimalPlaces, hasFixedDecimalPlaces, onChange, transformValue],
  );

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Permettiamo l'uso di tasti speciali
    if (e.metaKey || e.ctrlKey) return;
    // Bloccare input di caratteri non numerici o separatori decimali
    if (e.key.match(/^[^0-9,.]$/)) {
      e.preventDefault();
    }
  }, []);

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      focusedRef.current = true;
      propsOnFocus?.(e);
    },
    [propsOnFocus],
  );

  const handleBlur = useCallback(() => {
    focusedRef.current = false;
    onBlur();
  }, [onBlur]);

  const formatter = useCallback(
    (
      changedValue: any,
      info: {
        userTyping: boolean;
        input: string;
      },
    ) => {
      // Se l'utente sta scrivendo, uso il valore dell'input
      const inputVal = info.userTyping ? info.input : `${changedValue}`;
      if (!hasFixedDecimalPlaces) return inputVal.replace('.', ',');
      // Rimuovo le cifre decimali in eccesso
      const match = `${inputVal}`.replace(',', '.').match(decimalRegex);
      let formatted = match?.[0] ?? '';
      // toFixed solo al blur effettivo. Le ottimizzazioni performance
      // (yield-to-main, debounce) cambiano il timing dei re-render,
      // causando rc-input-number a chiamare il formatter con
      // userTyping=false anche durante la digitazione.
      // focusedRef discrimina blur reale da re-render controllato.
      if (!focusedRef.current && formatted) {
        formatted = new Decimal(formatted).toFixed(2);
      }
      return formatted.replace('.', ',');
    },
    [hasFixedDecimalPlaces, decimalRegex],
  );

  const parser = useCallback(
    (value: string | undefined) => {
      let parsed = value
        ? value.replace(/[^\d,.]+/g, '').replace(',', '.')
        : '';
      if (hasFixedDecimalPlaces && parsed.match(/\.\d{2}0+/)) {
        // Nel caso ci sono zeri dopo la seconda cifra decimale
        // concateno un 1 per forzare l'evento 'change'
        parsed += '1';
      }
      return parsed;
    },
    [hasFixedDecimalPlaces],
  );

  return (
    <InputNumber
      size="large"
      style={{ width: '100%' }}
      min={0}
      max={9_999_999_999_999}
      {...accessibility.attributes}
      ref={ref}
      {...inputProps}
      value={value}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      name={name}
      onKeyDown={onKeyDown}
      // Viene chiamato ogni volta che il valore di 'value' cambia
      formatter={formatter}
      pattern="[\d,]+"
      decimalSeparator=","
      parser={parser}
      prefix={!hideCurrency ? (currency ?? '€') : undefined}
      suffix={inputProps.suffix ?? <span />} // See https://ant.design/components/input#why-input-lose-focus-when-change-prefixsuffixshowcount
      disabled={visuallyDisabled}
    />
  );
}
